import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Play, Square, Coffee, Utensils, CheckCircle2, ExternalLink } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { fmtMinHM } from "@/lib/utils";

const apiErr = (e, fb) => e?.response?.data?.detail || fb;
const fmtMin = fmtMinHM;

/**
 * Acceso rápido al fichaje desde la cabecera: iniciar/pausar/reanudar/finalizar/
 * confirmar/reabrir la jornada sin entrar al módulo. Solo visible si el módulo está ACTIVO.
 */
export default function JornadaQuickAccess() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { data: cfg } = useQuery({
    queryKey: ["rh-config-qa"],
    queryFn: async () => (await api.get("/rh/config")).data,
    retry: false, staleTime: 5 * 60 * 1000,
  });
  const enabled = !!cfg?.enabled;
  const { data: today } = useQuery({
    queryKey: ["rh-today-qa"],
    queryFn: async () => (await api.get("/rh/jornada/today")).data,
    enabled, retry: false, refetchInterval: enabled ? 60000 : false,
  });
  if (!enabled) return null;

  const st = today?.state || "not_started";
  const openBreak = (today?.entries || []).find((e) => e.type === "break" && !e.end_time);
  const breakLabel = openBreak?.notes || "pausa";
  const onComida = (openBreak?.notes || "").toLowerCase().includes("comida");
  const map = {
    not_started: { t: "Fichar", dot: "bg-slate-400" },
    working: { t: "En jornada", dot: "bg-emerald-500 animate-pulse" },
    on_break: { t: onComida ? "En comida" : "En pausa", dot: "bg-amber-500" },
    ended: { t: "Confirmar", dot: "bg-blue-500" },
    confirmed: { t: "Jornada ✓", dot: "bg-primary" },
  }[st];

  const act = async (path) => {
    setBusy(true);
    try {
      await api.post(path);
      qc.invalidateQueries({ queryKey: ["rh-today-qa"] });
      qc.invalidateQueries({ queryKey: ["rh-today"] });
    } catch (e) { toast.error(apiErr(e, "Error")); } finally { setBusy(false); }
  };
  const startBreak = async (breakType) => {
    setBusy(true);
    try {
      await api.post("/rh/jornada/break/start", { break_type: breakType });
      qc.invalidateQueries({ queryKey: ["rh-today-qa"] }); qc.invalidateQueries({ queryKey: ["rh-today"] });
    } catch (e) { toast.error(apiErr(e, "Error")); } finally { setBusy(false); }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="jornada-quick-access"
          title="Registro horario"
          className="mr-1 flex items-center gap-2 rounded-lg border border-border px-2.5 py-1.5 text-xs font-semibold text-foreground transition-colors hover:bg-accent"
        >
          <span className={`h-2 w-2 rounded-full ${map.dot}`} />
          <Clock className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{map.t}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64" data-testid="jornada-quick-popover">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-semibold">Mi jornada</p>
          <span className="text-xs text-muted-foreground tabular-nums">{fmtMin(today?.duration_effective_min)}</span>
        </div>
        <div className="space-y-2">
          {st === "not_started" && (
            <Button className="w-full" disabled={busy} onClick={() => act("/rh/jornada/start")} data-testid="qa-start"><Play className="mr-1.5 h-4 w-4" />Iniciar jornada</Button>
          )}
          {st === "working" && (
            <>
              <Button variant="outline" className="w-full" disabled={busy} onClick={() => startBreak("Pausa")} data-testid="qa-break-start"><Coffee className="mr-1.5 h-4 w-4" />Iniciar pausa</Button>
              <Button variant="outline" className="w-full" disabled={busy} onClick={() => startBreak("Comida")} data-testid="qa-lunch-start"><Utensils className="mr-1.5 h-4 w-4" />Iniciar comida</Button>
              <Button variant="destructive" className="w-full" disabled={busy} onClick={() => act("/rh/jornada/end")} data-testid="qa-end"><Square className="mr-1.5 h-4 w-4" />Finalizar jornada</Button>
            </>
          )}
          {st === "on_break" && (
            <Button className="w-full" disabled={busy} onClick={() => act("/rh/jornada/break/end")} data-testid="qa-break-end"><Play className="mr-1.5 h-4 w-4" />Finalizar {breakLabel.toLowerCase()}</Button>
          )}
          {st === "ended" && (
            <>
              <Button className="w-full" disabled={busy} onClick={() => act("/rh/jornada/confirm")} data-testid="qa-confirm"><CheckCircle2 className="mr-1.5 h-4 w-4" />Confirmar jornada</Button>
              <Button variant="outline" className="w-full" disabled={busy} onClick={() => act("/rh/jornada/reopen")} data-testid="qa-reopen"><Play className="mr-1.5 h-4 w-4" />Reabrir jornada</Button>
            </>
          )}
          {st === "confirmed" && (
            <>
              <p className="text-center text-xs text-muted-foreground">Jornada confirmada ✓</p>
              <Button variant="outline" className="w-full" disabled={busy} onClick={() => act("/rh/jornada/reopen")} data-testid="qa-reopen"><Play className="mr-1.5 h-4 w-4" />Reabrir (la cerré por error)</Button>
            </>
          )}
          <Button variant="ghost" className="w-full text-muted-foreground" onClick={() => nav("/rrhh/registro-horario")} data-testid="qa-open-module"><ExternalLink className="mr-1.5 h-4 w-4" />Abrir Registro Horario</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
